import React from 'react';
import { Dialog, Box, IconButton, LinearProgress, Zoom, Fade } from '@mui/material';
import { CheckCircle2, XCircle, Loader2, X, AlertCircle, ArrowRight, ThumbsUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function SaveProgressModal({ isOpen, onClose, items = [], projectId }) {
  const navigate = useNavigate();
  const total = items.length;
  const saved = items.filter(i => i.status === 'saved').length;
  const errors = items.filter(i => i.status === 'error').length;
  const currentSaving = items.find(i => i.status === 'saving')?.name;
  const allDone = items.every(i => i.status === 'saved' || i.status === 'error');
  const pct = total > 0 ? Math.round((saved + errors) / total * 100) : 0;

  const handleGoToSummary = () => {
    onClose();
    navigate('/user/summary', { state: { project_id: projectId } });
  };

  return (
    <Dialog
      open={isOpen}
      onClose={allDone ? onClose : undefined}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: '28px', 
          p: 0, 
          overflow: 'hidden',
          backgroundColor: '#ffffff',
          boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.15)',
          border: '1px solid rgba(226, 232, 240, 0.8)'
        }
      }}
    >
      <Box sx={{ p: 4, textAlign: 'center', position: 'relative' }}>
        {allDone && (
          <IconButton 
            onClick={onClose} 
            sx={{ 
              position: 'absolute', 
              right: 16, 
              top: 16, 
              color: 'slate.400',
              bgcolor: 'slate.100',
              '&:hover': { bgcolor: 'slate.200', color: 'slate.700' },
              transition: 'all 0.2s'
            }}
          >
            <X size={18} />
          </IconButton>
        )}

        {/* Icon / Loader Display */}
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          {!allDone ? (
            <div className="relative flex items-center justify-center">
              <div className="absolute w-20 h-20 rounded-full bg-blue-50 animate-ping" />
              <Loader2 size={64} className="text-blue-600 animate-spin" strokeWidth={1.75} />
              <div className="absolute text-[13px] font-bold tracking-tight text-blue-700">{pct}%</div>
            </div>
          ) : errors === 0 ? (
            <Zoom in={true}>
              <div className="w-16 h-16 bg-emerald-50 border border-emerald-100 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/5">
                <CheckCircle2 size={36} className="text-emerald-600" />
              </div>
            </Zoom>
          ) : (
            <Zoom in={true}>
              <div className="w-16 h-16 bg-amber-50 border border-amber-100 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/5">
                <AlertCircle size={36} className="text-amber-600" />
              </div>
            </Zoom>
          )}
        </Box>

        {/* Title & Subtitle */}
        <h2 className="text-xl font-extrabold text-slate-900 tracking-tight mb-1.5 mt-3">
          {!allDone ? 'Synchronizing Data' : errors === 0 ? 'Changes Saved!' : 'Partial Sync Completed'}
        </h2>
        <p className="text-xs text-slate-500 mb-6 leading-relaxed px-2">
          {!allDone 
            ? `Securing ${total} active configuration lines into Project #${projectId}`
            : errors === 0 
              ? `Project #${projectId} has been successfully updated and is ready.`
              : `Successfully synced ${saved} items, but encountered ${errors} network error(s).`
          }
        </p>

        {/* Progress or Actions */}
        {!allDone ? (
          <Box sx={{ px: 1, mb: 1 }}>
            <LinearProgress 
              variant="determinate" 
              value={pct} 
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: 'slate.100',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  bgcolor: '#2563eb'
                }
              }}
            />
            <Fade in={!!currentSaving}>
              <div className="flex items-center justify-center gap-1.5 mt-3">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse" />
                <p className="text-[11px] font-semibold text-blue-600 uppercase tracking-wider truncate max-w-[220px]">
                  {currentSaving || 'Initializing queue...'}
                </p>
              </div>
            </Fade>
          </Box>
        ) : (
          <div className="flex flex-col gap-2.5">
            {errors === 0 && projectId && (
              <button
                onClick={handleGoToSummary}
                className="w-full py-3.5 px-4 rounded-xl font-bold text-xs uppercase tracking-wider transition-all shadow-lg shadow-blue-600/20 bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center gap-2 group"
              >
                <span>Go to summary</span>
                <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </button>
            )}
            <button
              onClick={onClose}
              className={`w-full py-3.5 px-4 rounded-xl font-bold text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2 shadow-lg ${
                errors === 0 
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-600/20' 
                  : 'bg-slate-900 hover:bg-slate-800 text-white'
              }`}
            >
              {errors === 0 ? (
                <>
                  <ThumbsUp size={15} />
                  <span>Great, Thanks!</span>
                </>
              ) : (
                <span>Review Errors & Close</span>
              )}
            </button>
          </div>
        )}

        {/* Error Details Box */}
        {allDone && errors > 0 && (
          <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-xl text-left">
            <div className="flex items-center gap-2 text-red-600 text-[11px] font-bold uppercase tracking-wider mb-1">
              <XCircle size={13} /> Connection Interrupted
            </div>
            <p className="text-[11px] text-red-700/80 leading-normal">Some fields failed to persist. You can safely close and re-attempt syncing.</p>
          </div>
        )}
      </Box>
    </Dialog>
  );
}